import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Loader } from '@krakentech/coral';

import PageError from '@/components/PageError';
import { useHasRestrictedAccess } from '@/hooks/accounts/useHasRestrictedAccess';
import { useSyncFeatureFlag } from '@/hooks/utils/useFeatureFlags';
import { FeatureNames } from '@/types/features';
import { INTERNAL_PATHS } from '@/utils/urls';

import { AccessRestrictionReasons } from './constants';

export const AccessRestrictionContext = createContext<
  { hasRestrictedAccessForBadDebt?: boolean | null } | undefined
>(undefined);

const internalPaths = Object.values(INTERNAL_PATHS);

const restrictedPathsForUsersInBadDebt = internalPaths.filter(
  (singlePath) =>
    'restrictions' in singlePath &&
    singlePath.restrictions.includes(AccessRestrictionReasons.InBadDebt)
);

export const restrictedPathNamesForUsersInBadDebt =
  restrictedPathsForUsersInBadDebt.map(
    (singlePath) => 'path' in singlePath && singlePath.path
  );

export function AccessRestrictionContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const {
    isLoading: isLoadingHasRestrictedAccessForBadDebt,
    isError: isErrorHasRestrictedAccessForBadDebt,
    data: hasRestrictedAccessForBadDebt,
    isSuccess: isSuccessHasRestrictedAccessForBadDebt,
  } = useHasRestrictedAccess();

  const { replace, pathname } = useRouter();

  const enabledBadDebtManagement = useSyncFeatureFlag(
    FeatureNames.BadDebtManagement
  );

  useEffect(() => {
    if (
      enabledBadDebtManagement &&
      hasRestrictedAccessForBadDebt &&
      restrictedPathNamesForUsersInBadDebt.includes(pathname)
    )
      replace(INTERNAL_PATHS.HOME.path);
  }, [
    enabledBadDebtManagement,
    hasRestrictedAccessForBadDebt,
    replace,
    pathname,
  ]);

  if (!enabledBadDebtManagement) {
    return (
      <AccessRestrictionContext.Provider
        value={{ hasRestrictedAccessForBadDebt: false }}
      >
        {children}
      </AccessRestrictionContext.Provider>
    );
  }

  if (isLoadingHasRestrictedAccessForBadDebt) {
    return <Loader type="linear" variant="indeterminate" />;
  }

  if (isErrorHasRestrictedAccessForBadDebt) {
    return <PageError />;
  }

  if (isSuccessHasRestrictedAccessForBadDebt) {
    return (
      <AccessRestrictionContext.Provider
        value={{ hasRestrictedAccessForBadDebt }}
      >
        {children}
      </AccessRestrictionContext.Provider>
    );
  }
}

export function useAccessRestrictionContext(): {
  hasRestrictedAccessForBadDebt: boolean;
} {
  const context = useContext(AccessRestrictionContext);

  if (context === undefined) {
    throw new Error(
      'useAccessRestrictionContext must be used within an AccessRestrictionContextProvider'
    );
  }

  return context as { hasRestrictedAccessForBadDebt: boolean };
}
